import { convertCoverageLinesTextToEnum } from '@common/utils';
import { isArray, isEmpty, isEqual, keyBy } from 'lodash';
import moment from 'moment/moment';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom-latest';
import { AppBar, BoxSkeleton, Button, Divider, Paper, SearchBar, Stack, Typography } from '@common-components';
import { useSearchCarrierSubmissions, useSearchSubmissionExtractedData } from 'hooks';
import { messages } from 'i18n';
import { CarrierSubmission } from 'types';
import { demoData, parseTimezoneLessDate, scrollToElementPosition } from 'utils';
import { BROKER_ROUTES, generateSubmissionQueueDetailsPath } from 'broker/broker-routes';
import PageHeader from 'broker/components/common/layout/PageHeader';
import NoItemsFound from 'broker/components/common/NoItemsFound';
import TablePagination from 'broker/components/common/TablePagination';
import SubmissionFilter from 'broker/components/SubmissionsView/SubmissionFilter';
import SubmissionViewNoResults from 'broker/components/SubmissionsView/SubmissionViewNoResults';
import MainScrollArea from 'broker/hoc/MainScrollArea';
import QueueTableHeader from './QueueTableHeader';
import QueueTableItem from './QueueTableItem';
import useQueueFilters, { QueueFilterType } from './useQueueFilters';
import useSelectedMarket from './useSelectedMarket';
import WorkingOnBanner from './WorkingOnBanner';

const MainScrollAreaId = 'queueDataGridScrollArea';

export default function SubmissionQueuePage() {
  const navigate = useNavigate();
  const { selectedMarket, selectedMarketId, isLoadingMarkets } = useSelectedMarket();

  const shouldFetch = !!selectedMarketId;
  const {
    items: carrierSubmissions,
    count,
    isLoading,
    isFetching,
    isError: isSubmissionsError,
    error: submissionsError,
  } = useSearchCarrierSubmissions({
    enabled: shouldFetch,
    filter: shouldFetch ? { marketId: selectedMarketId! } : undefined,
  });

  const {
    items: submissionExtractions,
    isLoading: isLoadingExtractions,
    isFetching: isFetchingExtractions,
  } = useSearchSubmissionExtractedData({
    enabled: shouldFetch,
  });

  const extractionsBySubmissionId = useMemo(
    () => keyBy(submissionExtractions, 'submissionId'),
    [submissionExtractions],
  );

  const enrichedItems = useMemo<CarrierSubmission[]>(
    () =>
      carrierSubmissions.map((item) => {
        const extractedData = extractionsBySubmissionId[item.submissionId]?.extractedData;
        if (!extractedData) {
          return item;
        }
        return {
          ...item,
          submission: {
            ...item.submission,
            dueDate: item.submission?.dueDate ?? parseTimezoneLessDate(extractedData?.effectiveDate?.value ?? ''),
            insuredName: item.submission?.insuredName ?? extractedData?.insuredName?.value,
            coverageLines:
              isEmpty(item.submission?.coverageLines) && isArray(extractedData?.coverageLines?.value)
                ? convertCoverageLinesTextToEnum(extractedData!.coverageLines!.value!)
                : item.submission?.coverageLines,
          },
          agentName: item.agentName ?? extractedData?.brokerName?.value,
        };
      }),
    [carrierSubmissions, extractionsBySubmissionId],
  );

  const {
    filters,
    filteredItems,
    search,
    setSearch,
    isFilterApplied,
    sortType,
    setSortType,
    paginationReset,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
  } = useQueueFilters(enrichedItems, isLoading || isLoadingExtractions, isFetching || isFetchingExtractions);

  const itemsSlice = filteredItems.slice(page * rowsPerPage - rowsPerPage, page * rowsPerPage);

  const handlePageChange = (value: number) => {
    setPage(value);
    scrollToElementPosition(MainScrollAreaId, { top: 0, left: 0 });
  };

  const onSwitchCarrier = () => navigate(`/${BROKER_ROUTES.SUBMISSION_SERVICES}`);

  if (!isLoadingMarkets && !selectedMarket) {
    navigate(`/${BROKER_ROUTES.SUBMISSION_SERVICES}`, { replace: true });
    return null;
  }

  const onQueueItemClick = (item: CarrierSubmission) => {
    navigate(generateSubmissionQueueDetailsPath(item.id));
  };

  return (
    <Stack height={1}>
      <AppBar
        position="static"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'common.white',
          color: 'text.primary',
          px: 3,
          py: 1,
        }}
        elevation={0}
      >
        <WorkingOnBanner selectedMarket={selectedMarket} onSwitchCarrier={onSwitchCarrier} />
      </AppBar>

      <PageHeader>
        <Stack direction="row" alignItems="baseline" gap={1}>
          <Typography variant="h4">{messages.submissionQueuePage.title}</Typography>
          <Typography color="text.secondary" variant="body2">
            {isFilterApplied || search.length > 1
              ? messages.general.outOfSubmissions(filteredItems.length.toString(), count.toString())
              : count}
          </Typography>
        </Stack>
      </PageHeader>

      <Stack flex={1} p={3} gap={2}>
        {(isLoading || carrierSubmissions.length > 0) && (
          <Stack direction="row" gap={1} alignItems="center">
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder={messages.submissionQueuePage.searchPlaceholder}
              id="opex-carrier-submissions-search"
              sx={{ width: 290 }}
            />
            <SubmissionFilter
              filter={filters[QueueFilterType.Insured]}
              title={messages.submissionQueuePage.filterHeaders.insured}
              showSearch
            />
            <SubmissionFilter
              filter={filters[QueueFilterType.Lob]}
              title={messages.submissionQueuePage.filterHeaders.coverageLines}
              showSearch
            />
            <SubmissionFilter
              filter={filters[QueueFilterType.EffectiveDate]}
              title={messages.submissionQueuePage.filterHeaders.effectiveDate}
            />
            <SubmissionFilter
              filter={filters[QueueFilterType.ApprovalStatus]}
              title={messages.submissionQueuePage.filterHeaders.approvalStatus}
            />
            {isFilterApplied && (
              <Button
                onClick={() => {
                  Object.values(filters).forEach((filter) => {
                    filter.handleSelectAll();
                    paginationReset();
                  });
                }}
              >
                {messages.buttons.clearFilters}
              </Button>
            )}
          </Stack>
        )}

        {isSubmissionsError && (
          <Stack p={3} gap={1}>
            <Typography variant="body1">{messages.submissionQueuePage.loadError}</Typography>
            <Typography variant="body2" color="text.secondary">
              {submissionsError instanceof Error ? submissionsError.message : messages.submissionQueuePage.unknownError}
            </Typography>
          </Stack>
        )}

        {!isSubmissionsError && (isLoading || carrierSubmissions.length > 0) ? (
          <Stack component={Paper} variant="outlined" overflow="hidden" flex="1 1 0">
            <MainScrollArea
              id={MainScrollAreaId}
              sx={{
                overflowY: 'auto',
                overflowX: 'hidden',
                flex: 1,
              }}
            >
              <QueueTableHeader sortType={sortType} setSortType={setSortType} />
              {isLoading ? (
                <Stack gap={2} p={2}>
                  {demoData(10).map((row, index) => (
                    <Stack gap={2} key={row.id}>
                      <BoxSkeleton isLoading sx={{ width: 1, height: 45 }} index={index} />
                      <Divider />
                    </Stack>
                  ))}
                </Stack>
              ) : (
                itemsSlice.map((item) => {
                  const extractedData = extractionsBySubmissionId[item.submissionId]?.extractedData;
                  const isInsuredNameExtracted = !!(
                    extractedData?.insuredName?.value &&
                    item.submission?.insuredName === extractedData.insuredName.value
                  );
                  const isCoverageLinesExtracted =
                    isArray(extractedData?.coverageLines?.value) &&
                    !isEmpty(extractedData?.coverageLines?.value) &&
                    isEqual(
                      [...(item.submission?.coverageLines ?? [])].sort(),
                      [...convertCoverageLinesTextToEnum(extractedData!.coverageLines!.value!)].sort(),
                    );

                  const parsedEffectiveDate = extractedData?.effectiveDate?.value
                    ? parseTimezoneLessDate(extractedData.effectiveDate.value)
                    : undefined;
                  const isEffectiveDateExtracted = !!(
                    parsedEffectiveDate &&
                    item.submission?.dueDate &&
                    moment(item.submission.dueDate).isSame(moment(parsedEffectiveDate), 'day')
                  );

                  const isBrokerNameExtracted = !!(
                    extractedData?.brokerName?.value && item.agentName === extractedData.brokerName.value
                  );
                  return (
                    <QueueTableItem
                      key={item.id}
                      item={item}
                      isInsuredNameExtracted={isInsuredNameExtracted}
                      isCoverageLinesExtracted={isCoverageLinesExtracted}
                      isEffectiveDateExtracted={isEffectiveDateExtracted}
                      isBrokerNameExtracted={isBrokerNameExtracted}
                      searchInput={search}
                      onClick={() => onQueueItemClick(item)}
                    />
                  );
                })
              )}
              {!isLoading && itemsSlice.length === 0 && <SubmissionViewNoResults />}
            </MainScrollArea>
            {filteredItems.length > rowsPerPage && (
              <>
                <Divider />
                <TablePagination
                  from={page * rowsPerPage - rowsPerPage + 1}
                  to={page * rowsPerPage}
                  outOf={filteredItems.length}
                  handleChange={handlePageChange}
                  count={Math.ceil(filteredItems.length / rowsPerPage)}
                  page={page}
                  setPage={setPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                />
              </>
            )}
          </Stack>
        ) : (
          !isSubmissionsError && !isLoading && <NoItemsFound title={messages.submissionQueuePage.emptyState} />
        )}
      </Stack>
    </Stack>
  );
}
